import { Icon } from '@pms-digital/design-system';
import { type ReactNode, useCallback } from 'react';

/**
 * The right-hand drawer that holds the three panels of §4 when the editor's
 * column is taken (refonte modulaire §7.1; maquette `editeur-protocole.html`,
 * `historyHTML`).
 *
 * §7.1, word for word: « L'historique, "utilisé dans" et le retrait sont les
 * trois panneaux de l'éditeur de critère, ouverts dans un TIROIR par le bouton
 * "Historique" : la colonne de droite de la page est prise par les règles de
 * l'étape, et c'est la seule divergence avec les éditeurs de critère et de
 * persona. » So this file holds no panel of its own - it is a surface, and its
 * children are the same three components the two-column editors mount in an
 * `<aside>`.
 *
 * ## It is a `dialog` by ROLE and not a `<dialog>` element
 *
 * A native `<dialog open>` kept in flow by React state, the same régime as the
 * three boxes and for the same reasons `versioned-dialog.tsx` gives at length:
 * `open` rather than `showModal()`, so React owns the mounting, and Escape wired
 * by hand because `open` alone does not wire it. `open` is the NON-modal form -
 * it makes nothing inert - which is what a drawer wants: « Passer en
 * production » is reached from the history inside this drawer, and its own box
 * opens beside it rather than under a modality it would have to fight.
 *
 * It is a `<dialog>` and not an `<aside role="dialog">` because the element and
 * the role are the same promise, and the a11y lint says so by name: an ARIA role
 * spelled over an element that already carries it is a second source for one
 * fact. The label of §7.1 rides `aria-label`, so
 * `getByRole('dialog', { name: 'Historique, utilisation et retrait' })` finds it
 * either way.
 *
 * The veil is painted with `--backdrop` - the one token of `styles.css` that
 * declares a dark surface in a light-only application.
 *
 * ## The focus lands on CLOSE, and that is the opposite of the boxes
 *
 * `versioned-dialog` focuses « Annuler » so a stray Enter never commits. Here
 * there is nothing to commit: the drawer's first control IS the close button,
 * it is the shortest way back out, and leaving the focus behind on the page
 * would make the whole surface unreachable by keyboard.
 *
 * ## Written here because the package has no drawer
 *
 * `@pms-digital/design-system` has a modal `Dialog` and no side sheet, modal or
 * not, so the surface stays local. Its close button stays hand-written too: 36 px,
 * where the package's icon-only `Button` is 44 px.
 */

export interface HistoryDrawerProps {
  /** The `data-testid` prefix of the object: « protocole » (`VersionedWording.entity`). */
  entity: string;
  /** The three panels, in the order the column would have stacked them. */
  children: ReactNode;
  onClose: () => void;
  /**
   * The drawer's accessible name, when it does not hold the three panels.
   *
   * A singleton (issue #148) has no retirement, so « Historique, utilisation
   * et retrait » would name a panel that is not there - the one lie a screen
   * reader would be told and a sighted operator never shown.
   */
  label?: string;
}

export function HistoryDrawer({
  entity,
  children,
  onClose,
  label = 'Historique, utilisation et retrait',
}: HistoryDrawerProps) {
  // A `useCallback` ref rather than `autoFocus` (the a11y lint refuses it) and
  // rather than an effect - a stable callback runs on attach and on nothing else.
  const focusOnMount = useCallback((node: HTMLButtonElement | null) => node?.focus(), []);

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-[var(--backdrop)]">
      {/*
        The veil is a BUTTON and not a click handler on the div above: a
        clickable region has to be reachable and named, and « Fermer » spelled
        twice would be two ways out with one name. It is `sr-only` to the eye
        and full-size to the pointer, which is what a veil is.
      */}
      <button
        type="button"
        aria-label="Fermer le tiroir"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />
      <dialog
        open
        aria-label={label}
        data-testid={`${entity}-tiroir`}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
        className="relative m-0 flex h-full w-full max-w-105 flex-col gap-5 overflow-y-auto border-gray-200 border-l-2 bg-white p-6 text-gray-800 shadow-lg"
      >
        <button
          ref={focusOnMount}
          type="button"
          aria-label="Fermer"
          data-testid={`${entity}-tiroir-fermer`}
          onClick={onClose}
          className="absolute top-3 right-3 inline-flex size-9 cursor-pointer items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
        >
          <Icon name="close" size={20} />
        </button>
        {children}
      </dialog>
    </div>
  );
}
